import {AbstractModel} from "./abstract-model";
import {JsonObject, JsonProperty} from "typescript-json-serializer";
import {dateToEsri, esriToDate} from "../utils/date-utils";
import {computed, makeObservable, observable} from "mobx";

export interface SteckbriefProps {
    guid?: string;
    fileName?: string;
    label?: string;
    date?: Date;
    fileSize?: number;
    bfsNr?: string;
    processSource?: string;
    name?: string;
    path?: string;
    riskType?: string;
    creator?: string;
    createdAt?: Date;
    editor?: string;
    updatedAt?: Date;
}

@JsonObject()
export class Steckbrief extends AbstractModel {

    static readonly comparableAttributes: string[] = [
        "fileName", "label", "date", "fileSize", "bfsNr", "processSource", "riskType"
    ];
    // workaround, because feature template doesn't seem to work for table features
    static readonly prototypeAttributes: string[] = [
        "guid", "fileName", "label", "date", "fileSize", "bfsNr", "processSource", "riskType"
    ];
    // field mappings
    static readonly databaseFieldNames: Map<string, string> = new Map<string, string>([
        ["objectId", "objectid"],
        ["guid", "guid"],
        ["label", "label"],     // for csv export to work
        ["fileName", "dateiname"],
        ["fileSize", "dateigroesse"],
        ["date", "datum"],
        ["bfsNr", "bfsnr"],
        ["hpa", "hauptprozess"],
        ["processSource", "processsource"],
        ["riskType", "risiko_typ"]
    ]);

    class = "Steckbrief";

    static sortByName(a: Steckbrief, b: Steckbrief) {
        if (a.label < b.label) {
            return -1;
        }
        if (a.label > b.label) {
            return 1;
        }

        // names must be equal
        return 0;
    }
    static sortByNameAndRiskType(a: Steckbrief, b: Steckbrief) {
        if (a.riskType < b.riskType) {
            return -1;
        }
        if (a.riskType > b.riskType) {
            return 1;
        }
        // If risk type are equal, compare label
        if (a.label < b.label) {
            return -1;
        }
        if (a.label > b.label) {
            return 1;
        }

        // names must be equal
        return 0;
    }

    @JsonProperty({name: "guid"})
        guid: string;

    /**
     * Anzeigename
     */
    @JsonProperty({name: "label"})
        label: string;

    /**
     * Dateiname
     */
    @JsonProperty({name: "dateiname"})
        fileName: string;

    /**
     * Dateigrösse
     */
    @JsonProperty({name: "dateigroesse"})
        fileSize: number;

    /**
     * Date of document
     */
    @JsonProperty({
        name: "datum",
        beforeDeserialize: esriToDate,
        afterSerialize: dateToEsri
    })
        date: Date;

    @JsonProperty({name: "bfsnr"})
        bfsNr: string;

    @JsonProperty({name: "hauptprozess"})
        hpa: string;

    @JsonProperty({name: "processsource"})
        processSource: string;

    @JsonProperty({name: "risiko_typ"})
        riskType: string;

    constructor(props?: SteckbriefProps) {
        super();
        if (props) {
            Object.assign(this, props);
        }
        // make observable after setting properties to avoid 'strict' messages
        makeObservable(this, {
            guid: observable,
            fileName: observable,
            fileSize: observable,
            label: observable,
            date: observable,
            bfsNr: observable,
            processSource: observable,

            isAlterable: computed,

            creator: observable,
            createdAt: observable,
            editor: observable,
            updatedAt: observable,
            isNewRecord: computed
        });
    }

    /**
     * Path to this document
     */
    get path(): string {
        return `${this.fileName}`;
    }

    get displayLabel(): string {
        return `${this.label}`;
    }

    get fileSizeLabel(): string {
        const mb = this.fileSize / (1024 * 1024); // Convert bytes to MB
        return `${mb.toFixed(2)} MB`;
    }

    get comparableAttributes(): string[] {
        return Steckbrief.comparableAttributes;
    }

    get isAlterable(): boolean {
        return false;
    }
}
